import { useState } from "react";
import type { ReactNode } from "react";
import type { Theme, FontPairing } from "@structurify/shared-types";
import { MonoLabel, QuietButton } from "../components/primitives/index.ts";
import { requestMagicLink } from "../api/auth.ts";

interface AuthProps {
  theme: Theme;
  fonts: FontPairing;
  mode: "signin" | "signup";
  onSubmit: () => void;
  onSwitch: () => void;
}

export function StageAuth({ theme, fonts, mode, onSwitch }: AuthProps) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isSignup = mode === "signup";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || loading) return;
    setLoading(true);
    setError(null);
    try {
      await requestMagicLink(email.trim().toLowerCase());
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-split" style={{ flex: 1, display: "flex", overflow: "auto" }}>
      <style>{`
        @media (max-width: 880px) {
          .auth-split { flex-direction: column !important; }
          .auth-split > div { border-right: none !important; }
          .auth-narrative { padding: clamp(28px, 6vw, 48px) !important; min-height: auto !important; }
          .auth-narrative-trust { display: none !important; }
          .auth-form-pane { padding: clamp(28px, 6vw, 48px) !important; border-top: 1px solid rgba(0,0,0,0.08) !important; }
        }
      `}</style>

      {/* Left narrative */}
      <div className="auth-narrative" style={{ flex: 1.1, padding: "clamp(36px, 6vw, 64px)", display: "flex", flexDirection: "column", justifyContent: "space-between", background: theme.bg, borderRight: `1px solid ${theme.rule}`, minHeight: "100%" }}>
        <MonoLabel theme={theme} fonts={fonts} size={10}>STRUCTURIFY</MonoLabel>

        <div style={{ maxWidth: 460, opacity: 0, animation: "fadeUp 600ms forwards ease-out" }}>
          <h1 style={{ fontFamily: fonts.sans, fontWeight: 500, fontSize: 44, lineHeight: 1.05, letterSpacing: "-0.025em", color: theme.ink, margin: 0 }}>
            {isSignup ? "Keep going." : "Welcome back."}
          </h1>
          <p style={{ fontFamily: fonts.sans, fontSize: 15, color: theme.inkSoft, lineHeight: 1.55, marginTop: 16 }}>
            {isSignup
              ? "You've seen what Structurify can do. Make an account to keep your workflows, run batches, and add your team."
              : "Sign in to pick up where you left off."}
          </p>

          {isSignup && (
            <div style={{ marginTop: 36, display: "grid", gap: 14, fontFamily: fonts.sans, fontSize: 13.5, color: theme.inkSoft, lineHeight: 1.5 }}>
              {[
                ["250 documents / month", "on the free plan, then $0.04 each"],
                ["Unlimited reusable workflows", "so the next batch takes seconds"],
                ["Source-grounded review", "every value traceable to its origin"],
                ["SOC 2 Type II", "documents never train models"],
              ].map(([h, s], i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "14px 1fr", gap: 12 }}>
                  <span style={{ width: 14, height: 14, marginTop: 2, border: `1px solid ${theme.ink}`, position: "relative" }}>
                    <svg width="10" height="10" viewBox="0 0 10 10" style={{ position: "absolute", top: 1, left: 1 }}>
                      <path d="M2 5 L4 7 L8 3" stroke={theme.ink} strokeWidth="1.25" fill="none" />
                    </svg>
                  </span>
                  <div>
                    <span style={{ color: theme.ink, fontWeight: 500 }}>{h}</span>
                    <span style={{ color: theme.inkMute }}> — {s}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="auth-narrative-trust" style={{ fontFamily: fonts.mono, fontSize: 10, color: theme.inkMute, letterSpacing: "0.06em", display: "flex", gap: 24, flexWrap: "wrap" }}>
          <span>SOC 2 TYPE II</span>
          <span>GDPR</span>
          <span>HIPAA-READY</span>
        </div>
      </div>

      {/* Right form */}
      <div className="auth-form-pane" style={{ flex: 0.9, padding: "clamp(36px, 6vw, 64px)", display: "flex", alignItems: "center", justifyContent: "center", background: theme.surface }}>
        <div style={{ width: "100%", maxWidth: 360 }}>
          {sent ? (
            <div style={{ opacity: 0, animation: "fadeUp 400ms forwards ease-out" }}>
              <MonoLabel theme={theme} fonts={fonts} size={9}>CHECK YOUR EMAIL</MonoLabel>
              <h2 style={{ fontFamily: fonts.sans, fontWeight: 500, fontSize: 26, letterSpacing: "-0.02em", color: theme.ink, margin: "14px 0 12px 0" }}>
                Link sent to {email}
              </h2>
              <p style={{ fontFamily: fonts.sans, fontSize: 14, color: theme.inkSoft, lineHeight: 1.65, margin: "0 0 28px 0" }}>
                Click the sign-in link in the email. It expires in 15 minutes and works once only. Check your spam folder if it doesn't arrive.
              </p>
              <button
                onClick={() => { setSent(false); setEmail(""); }}
                style={{ fontFamily: fonts.sans, fontSize: 13, color: theme.inkSoft, background: "transparent", border: "none", cursor: "pointer", padding: 0, borderBottom: `1px solid ${theme.rule}` }}
              >
                Use a different email
              </button>
            </div>
          ) : (
            <>
              <MonoLabel theme={theme} fonts={fonts} size={9}>{isSignup ? "CREATE ACCOUNT" : "SIGN IN"}</MonoLabel>

              <form onSubmit={(e) => { void submit(e); }} style={{ marginTop: 18 }}>
                <div style={{ marginBottom: 14 }}>
                  <MonoLabel theme={theme} fonts={fonts} size={9} style={{ display: "block", marginBottom: 6 }}>WORK EMAIL</MonoLabel>
                  <input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    autoFocus
                    required
                    style={{ width: "100%", fontFamily: fonts.sans, fontSize: 14, color: theme.ink, background: "transparent", border: "none", borderBottom: `1px solid ${theme.ruleStrong}`, padding: "8px 0", outline: "none" }}
                  />
                </div>

                {error && (
                  <p style={{ fontFamily: fonts.sans, fontSize: 12, color: theme.confidenceLow, margin: "0 0 12px 0" }}>
                    {error}
                  </p>
                )}

                <QuietButton theme={theme} fonts={fonts} primary style={{ width: "100%", justifyContent: "center", marginTop: 6, opacity: loading ? 0.6 : 1 }}>
                  {loading ? "Sending…" : `Send sign-in link →`}
                </QuietButton>
              </form>

              <p style={{ marginTop: 20, fontFamily: fonts.sans, fontSize: 12.5, color: theme.inkMute }}>
                {isSignup ? "Already have an account?" : "New here?"}{" "}
                <button onClick={onSwitch} style={{ background: "transparent", border: "none", cursor: "pointer", color: theme.ink, fontWeight: 500, padding: 0, fontFamily: "inherit", fontSize: "inherit", borderBottom: `1px solid ${theme.ink}` }}>
                  {isSignup ? "Sign in" : "Create an account"}
                </button>
              </p>

              {isSignup && (
                <p style={{ marginTop: 20, fontFamily: fonts.sans, fontSize: 11, color: theme.inkMute, lineHeight: 1.55 }}>
                  By continuing, you agree to the Terms of Service and Privacy Policy. Documents you upload are encrypted and never used to train models.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ----- Paywall -----

interface PaywallProps {
  theme: Theme;
  fonts: FontPairing;
  creditsUsed: number;
  freeLimit: number;
  onClose: () => void;
  onSignUp: () => void;
  onSignIn: () => void;
}

export function Paywall({ theme, fonts, creditsUsed, freeLimit, onClose, onSignUp, onSignIn }: PaywallProps) {
  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(10,10,9,0.30)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, backdropFilter: "blur(2px)", animation: "fadeIn 200ms ease-out" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: "100%", maxWidth: 520, background: theme.surface, border: `1px solid ${theme.rule}`, boxShadow: "0 24px 60px rgba(20,16,10,0.20)", padding: "40px 40px 32px", animation: "fadeUp 280ms ease-out" }}
      >
        <MonoLabel theme={theme} fonts={fonts} size={9}>FREE TRIAL · {creditsUsed} OF {freeLimit} USED</MonoLabel>
        <h3 style={{ fontFamily: fonts.sans, fontWeight: 500, fontSize: 28, lineHeight: 1.15, letterSpacing: "-0.02em", color: theme.ink, margin: "14px 0 12px" }}>
          You've used your three free documents.
        </h3>
        <p style={{ fontFamily: fonts.sans, fontSize: 14.5, color: theme.inkSoft, lineHeight: 1.55, margin: 0, marginBottom: 24 }}>
          Create a free account to keep going — 250 documents per month, no card required.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, background: theme.rule, border: `1px solid ${theme.rule}`, marginBottom: 28 }}>
          {[
            { tier: "FREE", price: "250 docs / mo", desc: "Workflows · CSV / JSON · API" },
            { tier: "TEAM", price: "$0.04 / doc", desc: "Beyond free · billed monthly" },
          ].map(({ tier, price, desc }) => (
            <div key={tier} style={{ background: theme.panel, padding: "14px 16px" }}>
              <MonoLabel theme={theme} fonts={fonts} size={9}>{tier}</MonoLabel>
              <div style={{ fontFamily: fonts.sans, fontSize: 22, color: theme.ink, fontWeight: 500, marginTop: 6 }}>{price}</div>
              <div style={{ fontFamily: fonts.sans, fontSize: 12, color: theme.inkMute, marginTop: 4 }}>{desc}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <QuietButton theme={theme} fonts={fonts} primary onClick={onSignUp} style={{ flex: 1, justifyContent: "center" }}>Create free account</QuietButton>
          <QuietButton theme={theme} fonts={fonts} onClick={onSignIn}>Sign in</QuietButton>
        </div>

        <button onClick={onClose} style={{ marginTop: 14, fontFamily: fonts.sans, fontSize: 12, color: theme.inkMute, background: "transparent", border: "none", cursor: "pointer", padding: 0 }}>
          Maybe later
        </button>
      </div>
    </div>
  );
}

// ----- AccountMenu -----

interface AccountMenuProps {
  theme: Theme;
  fonts: FontPairing;
  authed: boolean;
  userEmail?: string | undefined;
  creditsUsed: number;
  freeLimit: number;
  onClose: () => void;
  onSignIn: () => void;
  onSignUp: () => void;
  onSignOut: () => void;
}

export function AccountMenu({ theme, fonts, authed, userEmail, creditsUsed, freeLimit, onClose, onSignIn, onSignUp, onSignOut }: AccountMenuProps) {
  const remaining = Math.max(0, freeLimit - creditsUsed);
  const pct = creditsUsed / freeLimit;
  const initials = userEmail ? (userEmail.split("@")[0] ?? "?").slice(0, 1).toUpperCase() : "?";

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 90, background: "transparent" }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ position: "absolute", top: 60, right: 24, width: 320, background: theme.surface, border: `1px solid ${theme.rule}`, boxShadow: "0 16px 40px rgba(20,16,10,0.14)", animation: "fadeUp 200ms ease-out" }}
      >
        <div style={{ padding: "16px 18px", borderBottom: `1px solid ${theme.rule}` }}>
          {authed && userEmail ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: 32, background: theme.ink, color: theme.docPaper, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: fonts.sans, fontSize: 12, fontWeight: 500 }}>{initials}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: fonts.mono, fontSize: 11, color: theme.ink, letterSpacing: "0.02em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{userEmail}</div>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontFamily: fonts.sans, fontSize: 13, color: theme.ink, fontWeight: 500 }}>You're on a free trial.</div>
              <div style={{ fontFamily: fonts.mono, fontSize: 10, color: theme.inkMute, letterSpacing: "0.04em", marginTop: 4 }}>NO ACCOUNT · {remaining} OF {freeLimit} DOCS LEFT</div>
            </div>
          )}
        </div>

        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${theme.rule}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <MonoLabel theme={theme} fonts={fonts} size={9}>{authed ? "USAGE THIS MONTH" : "FREE TRIAL"}</MonoLabel>
            <span style={{ fontFamily: fonts.mono, fontSize: 11, color: theme.inkSoft }}>{authed ? `${creditsUsed} / 250` : `${creditsUsed} / ${freeLimit}`}</span>
          </div>
          <div style={{ height: 2, background: theme.rule, marginTop: 8, position: "relative" }}>
            <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${Math.min(100, (authed ? (creditsUsed / 250) * 100 : pct * 100))}%`, background: theme.ink }} />
          </div>
        </div>

        <div style={{ padding: "6px 0" }}>
          {authed ? (
            <>
              {["API & integrations", "Billing & plan", "Audit log", "Settings"].map((label) => (
                <MenuItemBtn key={label} theme={theme} fonts={fonts}>{label}</MenuItemBtn>
              ))}
              <div style={{ height: 1, background: theme.rule, margin: "6px 0" }} />
              <MenuItemBtn theme={theme} fonts={fonts} onClick={onSignOut}>Sign out</MenuItemBtn>
            </>
          ) : (
            <>
              <MenuItemBtn theme={theme} fonts={fonts} onClick={onSignUp} primary>Create account</MenuItemBtn>
              <MenuItemBtn theme={theme} fonts={fonts} onClick={onSignIn}>Sign in</MenuItemBtn>
              <div style={{ height: 1, background: theme.rule, margin: "6px 0" }} />
              <MenuItemBtn theme={theme} fonts={fonts} muted>API &amp; integrations</MenuItemBtn>
              <MenuItemBtn theme={theme} fonts={fonts} muted>Billing &amp; plan</MenuItemBtn>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MenuItemBtn({ children, onClick, primary, muted, theme, fonts }: { children: ReactNode; onClick?: () => void; primary?: boolean; muted?: boolean; theme: Theme; fonts: FontPairing }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ width: "100%", textAlign: "left", background: hover ? theme.panel : "transparent", border: "none", padding: "9px 18px", cursor: muted ? "default" : "pointer", fontFamily: fonts.sans, fontSize: 13, color: primary ? theme.ink : muted ? theme.inkMute : theme.inkSoft, fontWeight: primary ? 500 : 450, display: "flex", alignItems: "center", gap: 10 }}
    >
      <span style={{ flex: 1 }}>{children}</span>
      {!muted && <span style={{ color: theme.inkMute, fontFamily: fonts.mono, fontSize: 10 }}>›</span>}
    </button>
  );
}
